import { z } from 'zod';
import { ActivitySourceSchema } from '../activity/activities.schemas';
import { ActorSchema } from '../common/actor.schema';
import {
  AreaCodeSchema,
  ArchiveReasonSchema,
  MarketingTypeSchema,
  PricingSaleFieldSchema,
  PropertyStatusSchema,
} from '../property/properties.schemas';
import { PropertyCategorySchema, PropertySubtypeSchema } from '../common/property-taxonomy.schema';
import { DocumentRequestCategorySchema } from '../property/document-requests.schemas';
import { BROWSER_RUN_FAILURES, BROWSER_RUN_KINDS, BROWSER_RUN_SOURCES, PORTALS } from '../property/portal.schemas';

export const PropertyCreatedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  assignedMemberId: z.uuid(),
  title: z.string(),
  // IMM-432.3 — the shared Immobilientyp carried onto the created-event payload.
  category: PropertyCategorySchema,
  subtype: PropertySubtypeSchema.nullable(),
  marketingType: MarketingTypeSchema,
  status: PropertyStatusSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyCreatedPayload = z.infer<typeof PropertyCreatedPayloadSchema>;

export const PricingSaleFieldChangeSchema = z.object({
  field: PricingSaleFieldSchema,
  from: z.union([z.number(), z.string(), z.boolean()]).nullable(),
  to: z.union([z.number(), z.string(), z.boolean()]).nullable(),
});
export type PricingSaleFieldChange = z.infer<typeof PricingSaleFieldChangeSchema>;

export const AreaChangeSchema = z.object({
  field: AreaCodeSchema,
  from: z.number().nullable(),
  to: z.number().nullable(),
});
export type AreaChange = z.infer<typeof AreaChangeSchema>;

/**
 * Emitted after the property_pricing_sale row for a property is replaced.
 * Carries the identity bits plus the save's diff; subscribers that need the
 * current state still re-read it from the repository.
 */
export const PropertyPricingSaleUpdatedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  // Which surface made the save (IMM-1341, mirroring IMM-940): APP, or CONNECTED_CLIENT for a workspace-MCP tool call.
  // Defaulted so an event already in the outbox across the deploy parses as APP.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
  // The save's field-level diff (IMM-1142). Optional: an event already in the outbox
  // across the deploy carries none, and is shown without value lines.
  changes: z.array(PricingSaleFieldChangeSchema).optional(),
});

export type PropertyPricingSaleUpdatedPayload = z.infer<typeof PropertyPricingSaleUpdatedPayloadSchema>;

/**
 * Emitted after the property_areas rows for a property are replaced.
 * Same identity-plus-diff payload as the pricing-sale event.
 */
export const PropertyAreasUpdatedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  // Which surface made the save (IMM-1341, mirroring IMM-940): APP, or CONNECTED_CLIENT for a workspace-MCP tool call.
  // Defaulted so an event already in the outbox across the deploy parses as APP.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
  // See the pricing-sale event: optional because of events already in the outbox.
  changes: z.array(AreaChangeSchema).optional(),
});

export type PropertyAreasUpdatedPayload = z.infer<typeof PropertyAreasUpdatedPayloadSchema>;

/**
 * Emitted when a property is activated (DRAFT/INACTIVE → ACTIVE) — currently by
 * the deal-driven contract-received reaction (IMM-174). Identity-only payload;
 * consumers re-read the property for its latest state.
 */
export const PropertyActivatedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyActivatedPayload = z.infer<typeof PropertyActivatedPayloadSchema>;

/**
 * Emitted when a property is archived. Identity-only payload; consumers re-read
 * the property. IMM-1057.6's portal-listing subscriber withdraws the live listings.
 */
export const PropertyArchivedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyArchivedPayload = z.infer<typeof PropertyArchivedPayloadSchema>;

/**
 * Emitted when a property is deactivated. Same shape as archiving; IMM-1057.6's
 * portal-listing subscriber withdraws the live listings.
 */
export const PropertyDeactivatedPayloadSchema = PropertyArchivedPayloadSchema;

export type PropertyDeactivatedPayload = z.infer<typeof PropertyDeactivatedPayloadSchema>;

/**
 * One row of `property_status_changes`, published by every status writer in the
 * row's own transaction (IMM-1142). `statusChangeId` IS the row id, so a later
 * replay of the history converges on the same timeline entry.
 */
export const PropertyStatusChangedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  statusChangeId: z.uuid(),
  fromStatus: PropertyStatusSchema,
  toStatus: PropertyStatusSchema,
  // The row's IMM-392 values: chosen on an archive, cleared-from on an un-archive, null otherwise.
  reason: ArchiveReasonSchema.nullable(),
  note: z.string().nullable(),
  actor: ActorSchema,
  // Which surface made the save (IMM-1341, mirroring IMM-940): APP, or CONNECTED_CLIENT for a workspace-MCP tool call.
  // Defaulted so an event already in the outbox across the deploy parses as APP.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type PropertyStatusChangedPayload = z.infer<typeof PropertyStatusChangedPayloadSchema>;

/**
 * Emitted after a property is deleted (soft-delete). Drives cross-sub-resource
 * cleanup the FK cascade cannot reach on a soft-delete — e.g. reaping the
 * property's documents and their object-storage bytes (IMM-218.1). Identity-only
 * payload; subscribers act on the ids.
 */
export const PropertyDeletedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PropertyDeletedPayload = z.infer<typeof PropertyDeletedPayloadSchema>;

/**
 * Emitted when an agent raises a Dokumentenanforderung (IMM-383.2) — the hand-off
 * to IMM-412, which composes/brands/sends the owner e-mail. Carries the resolved
 * request so the consumer needs no property re-read to build the message.
 */
export const DocumentRequestCreatedPayloadSchema = z.object({
  requestId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  recipientContactId: z.uuid(),
  requestedByMemberId: z.uuid(),
  categories: z.array(DocumentRequestCategorySchema),
  note: z.string().nullable(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type DocumentRequestCreatedPayload = z.infer<typeof DocumentRequestCreatedPayloadSchema>;

/**
 * Emitted by IMM-1057.4's runner when a successful transmission puts an Objekt's FIRST listing online
 * (no listing of the Objekt had `onlineSince` before). The deal module advances the Objekt's one ACTIVE
 * Verkauf-Deal to ONLINE (IMM-1057.9). Never raised for a retry, a re-publication, an update, or the
 * second listing of a Mehrfamilienhaus pair.
 */
export const PortalListingFirstPublishedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  listingId: z.uuid(),
  portal: z.enum(PORTALS),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PortalListingFirstPublishedPayload = z.infer<typeof PortalListingFirstPublishedPayloadSchema>;

// IMM-1381 (ADR 0064): the browser extension wrote this listing on ImmoScout24 and immoOS recorded its report.
export const PortalListingBrowserTransmittedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  listingId: z.uuid(),
  portalListingId: z.string(),
  live: z.boolean(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PortalListingBrowserTransmittedPayload = z.infer<typeof PortalListingBrowserTransmittedPayloadSchema>;

// IMM-1408: „Verknüpfen“ kept a Scout-ID on the Objekt; nothing was written to ImmoScout24.
export const PortalListingBrowserLinkedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  listingId: z.uuid(),
  portalListingId: z.string(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PortalListingBrowserLinkedPayload = z.infer<typeof PortalListingBrowserLinkedPayloadSchema>;

// IMM-1406: the browser extension started or ended a run on the Objekt's ImmoScout24 listing.
export const PortalListingBrowserRunRequestedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  runId: z.uuid(),
  kind: z.enum(BROWSER_RUN_KINDS),
  source: z.enum(BROWSER_RUN_SOURCES),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PortalListingBrowserRunRequestedPayload = z.infer<typeof PortalListingBrowserRunRequestedPayloadSchema>;

export const PortalListingBrowserRunFinishedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  runId: z.uuid(),
  kind: z.enum(BROWSER_RUN_KINDS),
  status: z.enum(['DONE', 'FAILED']),
  failure: z.enum(BROWSER_RUN_FAILURES).nullable(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PortalListingBrowserRunFinishedPayload = z.infer<typeof PortalListingBrowserRunFinishedPayloadSchema>;

// IMM-1406.4: the member confirmed or declined a client's request on the ImmoScout24 row.
export const PortalListingBrowserRunDecidedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  runId: z.uuid(),
  kind: z.enum(BROWSER_RUN_KINDS),
  decision: z.enum(['CONFIRMED', 'DECLINED']),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type PortalListingBrowserRunDecidedPayload = z.infer<typeof PortalListingBrowserRunDecidedPayloadSchema>;

// A raw, unformatted value as stored on the section — an enum token, a number, an
// ISO date (YYYY-MM-DD), a boolean, a sorted token list, or null for "empty".
// Formatting is the reader's job (IMM-1143.3), so a label rename never rewrites history.
export const PropertyChangeValueSchema = z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]);

export type PropertyChangeValue = z.infer<typeof PropertyChangeValueSchema>;

// One changed field. `item` addresses a row of an itemised section (an amenity
// code, an additional-cost type — IMM-1143.2); absent for a plain field.
// `TEXT` is free text recorded as "changed" only — never its content (IMM-1143.2).
export const PropertyFieldChangeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('VALUE'),
    field: z.string().min(1),
    item: z.string().min(1).optional(),
    from: PropertyChangeValueSchema,
    to: PropertyChangeValueSchema,
  }),
  z.object({
    kind: z.literal('TEXT'),
    field: z.string().min(1),
    item: z.string().min(1).optional(),
  }),
]);

export type PropertyFieldChange = z.infer<typeof PropertyFieldChangeSchema>;

// The sections whose saves publish `property.section-updated`. Same tokens as the
// MCP `property_update` section enum; PRICING_SALE and AREAS keep their own events.
export const PROPERTY_CHANGE_SECTIONS = [
  'IDENTITY',
  'ADDRESS',
  'BUILDING',
  'ENERGY',
  'INVESTMENT_YIELD',
  'PLOT_DETAILS',
  'AMENITIES',
  'PRICING_RENTAL',
  'RENTAL_TERMS',
  'ADDITIONAL_COSTS',
  'DESCRIPTIONS',
  'REFERENCE_SETTINGS',
] as const;

export const PropertyChangeSectionSchema = z.enum(PROPERTY_CHANGE_SECTIONS);

export type PropertyChangeSection = z.infer<typeof PropertyChangeSectionSchema>;

// The fields each section's record may name — the recorded-field maps of IMM-1143.1/.2,
// TEXT fields included. The change history's label table is typed against these (IMM-1143.3),
// and a backend test pins them to the maps, so a newly recorded field fails the build until labelled.
export const PROPERTY_CHANGE_SECTION_FIELDS = {
  IDENTITY: ['title', 'marketingType', 'referenceNumber', 'availableFrom', 'category', 'subtype', 'commercialFineType'],
  ADDRESS: [
    'street',
    'houseNumber',
    'postalCode',
    'city',
    'district',
    'state',
    'country',
    'parcelNumber',
    'parcelSubdivision',
    'hideExactAddress',
    'latitude',
    'longitude',
  ],
  BUILDING: ['yearBuilt', 'lastRenovated', 'condition', 'interiorQuality', 'constructionPhase'],
  ENERGY: [
    'certificateBasis',
    'certificateYear',
    'energyClass',
    'energyValue',
    'primaryEnergyValue',
    'includesWarmWater',
    'certificateIssuedAt',
    'certificateValidUntil',
    'heatingType',
    'firingType',
  ],
  INVESTMENT_YIELD: [
    'isRented',
    'rentalIncome',
    'rentalIncomeTarget',
    'rentalIncomeActual',
    'priceMultiplier',
    'numberOfApartments',
    'numberOfCommercialUnits',
    'occupancyRate',
    'numberOfVacancies',
    'tenantStructure',
    'walt',
  ],
  PLOT_DETAILS: [
    'groundAreaRatio',
    'floorAreaRatio',
    'buildingMassRatio',
    'hasBuildingPermission',
    'shortTermBuildable',
    'requiresDemolition',
    'siteDevelopmentType',
    'siteConstructibleType',
  ],
  AMENITIES: ['value'],
  PRICING_RENTAL: [
    'quotedAs',
    'baseRent',
    'pricePerSqm',
    'totalRent',
    'heatingCostsIncluded',
    'vatTreatment',
    'vatRate',
    'compensationPayment',
    'rentFreeMonths',
    'fitOutContribution',
    'renewalOption',
    'priceOnInquiry',
    'ancillaryCosts',
    'ancillaryCostsType',
    'heatingCosts',
    'depositAmount',
    'depositMonths',
    'parkingRent',
    'hasCommission',
    'commission',
    'commissionNote',
  ],
  RENTAL_TERMS: ['furnishing', 'leaseTerm', 'fixedTermUntil', 'minimumTermMonths', 'rentType', 'subletting'],
  ADDITIONAL_COSTS: ['amount', 'perSqm', 'vatTreatment', 'note'],
  DESCRIPTIONS: [
    'description',
    'equipmentDescription',
    'locationDescription',
    'miscellaneous',
    'exposeTitle',
    'highlights',
  ],
  REFERENCE_SETTINGS: ['isReference', 'publishOnHomepage'],
} as const satisfies Record<PropertyChangeSection, ReadonlyArray<string>>;

export type PropertyChangeSectionField<S extends PropertyChangeSection> =
  (typeof PROPERTY_CHANGE_SECTION_FIELDS)[S][number];

export const PROPERTY_CONTACT_LINK_CHANGE_FIELDS = ['role', 'note'] as const;

/**
 * Emitted when a save of one Objekt section changed at least one value (IMM-1143).
 * The record IS the payload — the audit row stores it verbatim and the change
 * history (IMM-1142 / IMM-1143.3) renders it. Never emitted for a no-op save.
 */
export const PropertySectionUpdatedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  section: PropertyChangeSectionSchema,
  changes: z.array(PropertyFieldChangeSchema).min(1),
  actor: ActorSchema,
  // Which surface made the save (IMM-1341, mirroring IMM-940): APP, or CONNECTED_CLIENT for a workspace-MCP tool call.
  // Defaulted so an event already in the outbox across the deploy parses as APP.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type PropertySectionUpdatedPayload = z.infer<typeof PropertySectionUpdatedPayloadSchema>;

/**
 * Emitted when the role and/or note of an existing Objekt↔Kontakt link changed
 * (IMM-1143). The Kontakt is referenced by id only — its name is read when the
 * entry is shown, so no personal data enters the audit log (ADR 0015). The note
 * is recorded as changed, never by content.
 */
export const PropertyContactLinkUpdatedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  contactId: z.uuid(),
  changes: z.array(PropertyFieldChangeSchema).min(1),
  actor: ActorSchema,
  // Which surface made the save (IMM-1341, mirroring IMM-940): APP, or CONNECTED_CLIENT for a workspace-MCP tool call.
  // Defaulted so an event already in the outbox across the deploy parses as APP.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type PropertyContactLinkUpdatedPayload = z.infer<typeof PropertyContactLinkUpdatedPayloadSchema>;

/** IMM-1145 — how the hand mark changed. LIFTED_ON_LEAVING_ACTIVE is published by IMM-1145.3. */
export const PROPERTY_HAND_RESERVATION_CHANGES = ['MARKED', 'UNMARKED', 'LIFTED_ON_LEAVING_ACTIVE'] as const;

/**
 * Emitted in the writer's transaction whenever the hand mark changes (IMM-1145). `changeId` is minted per
 * change by the writer, so a redelivery converges on one timeline entry.
 */
export const PropertyHandReservationChangedPayloadSchema = z.object({
  propertyId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  changeId: z.uuid(),
  change: z.enum(PROPERTY_HAND_RESERVATION_CHANGES),
  // The status the Objekt left ACTIVE for; null unless change is LIFTED_ON_LEAVING_ACTIVE.
  toStatus: PropertyStatusSchema.nullable(),
  actor: ActorSchema,
  // Which surface made the save (IMM-1341, mirroring IMM-940): APP, or CONNECTED_CLIENT for a workspace-MCP tool call.
  // Defaulted so an event already in the outbox across the deploy parses as APP.
  source: ActivitySourceSchema.default('APP'),
  occurredAt: z.iso.datetime(),
});

export type PropertyHandReservationChangedPayload = z.infer<typeof PropertyHandReservationChangedPayloadSchema>;
